import { CSSProperties, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Logger from "Logger";
import PostAuthRoutes from "./PostAuthRoutes";
import useUser from 'Hooks/useUser';
import useOrganization from 'Hooks/useOrganization';
import useBrand from 'Hooks/useBrand';
import { useHistory } from "react-router-dom";
import Navbar from "components/Navbar/Navbar";
import UpgradeModalProvider from "Hooks/UpgradeModalContext";
import NavbarProvider from "pages/MarketingPage/Data/NavbarProvider";
import AlertContext from "Hooks/AlertContext";
import useScreen from "Hooks/useScreen";
import LoaderOverlay from "elements/LoaderOverlay";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import { CurrentBrand, OrganizationInvitationStatus, OrganizationStatus } from "modules/Types";
import { styx } from "modules/Utils";
import DebugBox from "components/shareable/DebugBox";

const InitializeApp = () => {
    const history = useHistory();
    const {
        isAuthenticated: isAuthenticatedWithAuth0,
        isLoading: isAuth0Loading,
        logout,
        loginWithRedirect: redirectToAuth0LoginPage,
        getAccessTokenSilently,
        user: userAuth0,
    } = useAuth0();

    const {
        currentUser,
        getCurrentUser,
        isLoadingUser,
    } = useUser();

    const {
        currentBrand,
        getBrands,
        isLoadingBrands,
        loadedBrands,
    } = useBrand();

    const {
        // organization,
        // isOrganizationInitialized,
        getOrganization: getOrganization_OLD, // TODO CONNER: only used for billing tab right now
    } = useOrganization();

    const {
        organization,
        isLoadingOrganization: isLoadingOrganizationV2,
        isOrganizationInitialized, // TODO CONNER: put back when using V2!
        getOrganization: getOrganizationV2,
        checkOrganizationUserInvitationStatus,
        setInvitationStatus,
    } = useOrganizationV2();

    const { cssCalc } = useScreen();

    /** Initializes the user session with Auth0 and database details */
    const initializeUserSession = async () => {
        // console.log('initializeUserSession >>>>');
        try {
            // Wait for Auth0 to finish loading before making any decisions
            if (isAuth0Loading) {
                return;
            }

            // Only redirect if we're definitely not authenticated
            if (!isAuthenticatedWithAuth0) {
                // console.log('initializeUserSession >>>> not authenticated');
                const params = new URLSearchParams(window.location.search);
                const authCode = params.get('auth_code');

                // If we have a TikTok auth code, prioritize redirecting to the TikTok callback
                if (authCode) {
                    // Store auth_code in localStorage for the callback route to use
                    localStorage.setItem('tt_auth_code', authCode);

                }

                redirectToAuth0LoginPage({
                    redirectUri: `${window.location.origin}`
                });
                return;
            }

            // Add guards against re-initialization
            const userSubAuth0 = userAuth0?.sub;
            if (!userSubAuth0) {
                // console.log('initializeUserSession >>>> isLoadingUser || currentUser');
                return;
            }

            const currentUserLocal = await getCurrentUser(
                true,
                userSubAuth0,
                getAccessTokenSilently,
                logout,
            );

            const currentBrandLocal = await getBrands(true, true) as CurrentBrand;

            const organizationV2Data = await getOrganizationV2({
                brandId: currentBrandLocal?.id,
                userId: currentUserLocal?.id,
            });
            const organizationId = organizationV2Data?.organization.id
            if (organizationV2Data && organizationId) {
                getOrganization_OLD(organizationId);
                const organizationUsersLocal = organizationV2Data?.organizationUsers;
                const invitationStatusLocal = organizationUsersLocal?.find(ou => ou.user_id === currentUserLocal?.id)?.invitation_status;

                if (invitationStatusLocal === OrganizationInvitationStatus.PENDING) {
                    history.push('/team');
                }
            }
        } catch (err) {
            Logger.error('Error initializing session:', err);
            if (!isAuthenticatedWithAuth0) {
                logout();
            }
        }
    };

    useEffect(() => {
        if (!isAuth0Loading) {
            initializeUserSession();
        }
    }, [isAuth0Loading, isAuthenticatedWithAuth0, logout]);

    useEffect(() => {
        if (!currentUser || !loadedBrands) {
            return;
        }

        if (!currentUser.email) {
            history.push('/email');
            return;
        }

        if (!loadedBrands.length) {
            history.push('/add-artist');
            return;
        }

        if (organization?.setup_status === OrganizationStatus.WAITING_FOR_DETAILS) {
            history.push('/team');
            return;
        }
    }, [currentUser, loadedBrands, organization]);

    const debugData = {
        isAuth0Loading,
        isLoadingUser,
        isAuthenticatedWithAuth0,
        isLoadingBrands,
        userAuth0,
        organization,
        currentUser,
        loadedBrands,
        currentBrand,
    }

    const renderLoader = () => {
        return (
            <LoaderOverlay
                className="loaderFullScreenInitializeApp1"
                title=""
            >
                <div
                    className="welcomeToSymphony"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Welcome to Symphony
                </div>
            </LoaderOverlay>
        )
    }

    const renderMainApp = () => {
        return (
            <div
                className="initializeAppComponent"
                style={styx(
                    sty.initializeAppComponent,
                    {
                        height: cssCalc()
                    }
                )}
            >
                <UpgradeModalProvider>
                    <AlertContext>
                        <NavbarProvider>
                            <Navbar className="navbarFromInitializeApp" />
                            <PostAuthRoutes />
                        </NavbarProvider>
                    </AlertContext>
                </UpgradeModalProvider>
            </div>
        )
    }

    if (currentUser && loadedBrands && isOrganizationInitialized) {
        return renderMainApp()
    }

    return renderLoader();
};

const sty: Record<string, CSSProperties> = {
    initializeAppComponent: {
        display: "flex",
        width: "100%",
    }
}

export default InitializeApp;